import { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Instagram, Download, Copy, ExternalLink, Loader2 } from "lucide-react";

const CANVAS_SIZE = 1080;          // square IG feed
const STORY_W = 1080, STORY_H = 1920; // IG story

function wrapText(ctx, text, maxWidth) {
  const words = (text || "").split(/\s+/).filter(Boolean);
  const lines = [];
  let line = "";
  words.forEach((w) => {
    const test = line ? `${line} ${w}` : w;
    if (ctx.measureText(test).width > maxWidth && line) {
      lines.push(line);
      line = w;
    } else {
      line = test;
    }
  });
  if (line) lines.push(line);
  return lines;
}

async function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}

function resolveImageSrc(src) {
  // Our own uploaded images (/api/uploads/{id}) are served with permissive CORS —
  // load them directly. Only truly external hosts go through the SSRF-guarded proxy.
  const apiBase = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
  if (!src) return src;
  if (src.startsWith("/api/")) return `${apiBase}${src}`;
  if (src.startsWith(apiBase)) return src;
  if (/^https?:\/\//i.test(src)) return `${apiBase}/api/image/proxy?url=${encodeURIComponent(src)}`;
  return src;
}

function drawCoverFill(ctx, img, cw, ch) {
  // object-fit: cover
  const r = Math.max(cw / img.width, ch / img.height);
  const w = img.width * r;
  const h = img.height * r;
  ctx.drawImage(img, (cw - w) / 2, (ch - h) / 2, w, h);
}

export default function ShareToInstagram({ open, onOpenChange, post }) {
  const feedCanvasRef = useRef(null);
  const storyCanvasRef = useRef(null);
  const [generating, setGenerating] = useState(false);
  const [format, setFormat] = useState("feed"); // "feed" or "story"
  const [caption, setCaption] = useState("");

  const origin = typeof window !== "undefined" ? window.location.origin : "https://betterthroughboxing.com";
  const postUrl = post ? `${origin}/api/share/blog/${post.slug}` : "";

  // Build default caption
  useEffect(() => {
    if (!post) return;
    const hashtags = [
      "#betterthroughboxing", "#boxing", "#boxingtraining", "#boxinglife",
      "#flowstatemethod", "#privatecoach",
      ...(post.keywords || []).slice(0, 4).map((k) => "#" + k.toLowerCase().replace(/[^a-z0-9]+/g, "")),
    ].filter(Boolean).slice(0, 12).join(" ");
    setCaption(
      `${post.title}\n\n` +
      `${post.excerpt}\n\n` +
      `Read the full breakdown ↓\n${postUrl}\n\n` +
      `Ready to train? DM to book a virtual session.\n\n` +
      hashtags
    );
  }, [post, postUrl]);

  // Render canvases when dialog opens or format changes
  useEffect(() => {
    if (!open || !post) return;
    let cancelled = false;
    setGenerating(true);

    (async () => {
      try {
        const img = await loadImage(resolveImageSrc(post.cover));
        if (cancelled) return;

        // FEED 1080x1080
        const fc = feedCanvasRef.current;
        if (fc) {
          fc.width = CANVAS_SIZE; fc.height = CANVAS_SIZE;
          const ctx = fc.getContext("2d");
          ctx.fillStyle = "#050505";
          ctx.fillRect(0, 0, CANVAS_SIZE, CANVAS_SIZE);
          drawCoverFill(ctx, img, CANVAS_SIZE, CANVAS_SIZE);
          // dark gradient from bottom
          const grad = ctx.createLinearGradient(0, CANVAS_SIZE * 0.35, 0, CANVAS_SIZE);
          grad.addColorStop(0, "rgba(5,5,5,0)");
          grad.addColorStop(0.55, "rgba(5,5,5,0.7)");
          grad.addColorStop(1, "rgba(5,5,5,0.96)");
          ctx.fillStyle = grad;
          ctx.fillRect(0, 0, CANVAS_SIZE, CANVAS_SIZE);
          // red accent bar
          ctx.fillStyle = "#FF331F";
          ctx.fillRect(60, CANVAS_SIZE - 360, 8, 220);
          // Category eyebrow
          ctx.fillStyle = "#FF331F";
          ctx.font = "700 22px 'Outfit', system-ui, sans-serif";
          ctx.textBaseline = "alphabetic";
          ctx.fillText((post.category || "Training").toUpperCase(), 92, CANVAS_SIZE - 340);
          // Title (Oswald display)
          ctx.fillStyle = "#FFFFFF";
          ctx.font = "700 86px 'Oswald', 'Impact', sans-serif";
          const lines = wrapText(ctx, (post.title || "").toUpperCase(), CANVAS_SIZE - 184);
          let y = CANVAS_SIZE - 280;
          lines.slice(0, 4).forEach((ln) => { ctx.fillText(ln, 92, y); y += 92; });
          // Bottom bar — brand
          ctx.fillStyle = "#FFFFFF";
          ctx.font = "700 20px 'Outfit', system-ui, sans-serif";
          ctx.fillText("BETTERTHROUGHBOXING.COM", 92, CANVAS_SIZE - 80);
          ctx.fillStyle = "#a1a1aa";
          ctx.font = "500 18px 'Outfit', system-ui, sans-serif";
          ctx.fillText("@flow_state_method", 92, CANVAS_SIZE - 52);
          // CTA chip top-right
          ctx.fillStyle = "#FF331F";
          ctx.fillRect(CANVAS_SIZE - 280, 60, 220, 56);
          ctx.fillStyle = "#FFFFFF";
          ctx.font = "700 22px 'Oswald', 'Impact', sans-serif";
          ctx.textAlign = "center";
          ctx.fillText("READ ON SITE", CANVAS_SIZE - 170, 96);
          ctx.textAlign = "left";
        }

        // STORY 1080x1920
        const sc = storyCanvasRef.current;
        if (sc) {
          sc.width = STORY_W; sc.height = STORY_H;
          const ctx = sc.getContext("2d");
          ctx.fillStyle = "#050505";
          ctx.fillRect(0, 0, STORY_W, STORY_H);
          // Top image block (upper 55%)
          const topH = Math.round(STORY_H * 0.55);
          drawCoverFill(ctx, img, STORY_W, topH);
          const tgrad = ctx.createLinearGradient(0, topH * 0.4, 0, topH);
          tgrad.addColorStop(0, "rgba(5,5,5,0)");
          tgrad.addColorStop(1, "rgba(5,5,5,0.85)");
          ctx.fillStyle = tgrad;
          ctx.fillRect(0, 0, STORY_W, topH);
          // Lower content area
          ctx.fillStyle = "#FF331F";
          ctx.fillRect(80, topH + 40, 8, 240);
          ctx.fillStyle = "#FF331F";
          ctx.font = "700 26px 'Outfit', system-ui, sans-serif";
          ctx.fillText((post.category || "Training").toUpperCase(), 116, topH + 80);
          ctx.fillStyle = "#FFFFFF";
          ctx.font = "700 96px 'Oswald', 'Impact', sans-serif";
          const slines = wrapText(ctx, (post.title || "").toUpperCase(), STORY_W - 232);
          let sy = topH + 160;
          slines.slice(0, 5).forEach((ln) => { ctx.fillText(ln, 116, sy); sy += 104; });
          // Excerpt
          ctx.fillStyle = "#d4d4d8";
          ctx.font = "400 32px 'Outfit', system-ui, sans-serif";
          const exLines = wrapText(ctx, post.excerpt || "", STORY_W - 232);
          let ey = sy + 30;
          exLines.slice(0, 4).forEach((ln) => { ctx.fillText(ln, 116, ey); ey += 42; });
          // CTA box
          ctx.fillStyle = "#FF331F";
          ctx.fillRect(80, STORY_H - 280, STORY_W - 160, 100);
          ctx.fillStyle = "#FFFFFF";
          ctx.font = "700 44px 'Oswald', 'Impact', sans-serif";
          ctx.textAlign = "center";
          ctx.fillText("READ THE FULL POST", STORY_W / 2, STORY_H - 215);
          ctx.textAlign = "left";
          ctx.fillStyle = "#FFFFFF";
          ctx.font = "700 26px 'Outfit', system-ui, sans-serif";
          ctx.textAlign = "center";
          ctx.fillText("BETTERTHROUGHBOXING.COM", STORY_W / 2, STORY_H - 140);
          ctx.fillStyle = "#a1a1aa";
          ctx.font = "500 22px 'Outfit', system-ui, sans-serif";
          ctx.fillText("@flow_state_method", STORY_W / 2, STORY_H - 100);
          ctx.textAlign = "left";
        }
      } catch (err) {
        toast.error("Couldn't load cover image. Try a different cover or check the URL.");
      } finally {
        if (!cancelled) setGenerating(false);
      }
    })();

    return () => { cancelled = true; };
  }, [open, post, format]);

  const download = () => {
    const canvas = format === "feed" ? feedCanvasRef.current : storyCanvasRef.current;
    if (!canvas) return;
    canvas.toBlob((blob) => {
      if (!blob) return toast.error("Couldn't generate image");
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `btb-${post.slug}-${format}.png`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Image saved — ready to post on Instagram");
    }, "image/png");
  };

  const copyCaption = async () => {
    try {
      await navigator.clipboard.writeText(caption);
      toast.success("Caption copied to clipboard");
    } catch {
      toast.error("Couldn't copy. Select and copy manually.");
    }
  };

  if (!post) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0F0F0F] border-[#27272A] text-white max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="share-instagram-dialog">
        <DialogHeader>
          <DialogTitle className="font-display uppercase flex items-center gap-2"><Instagram className="h-5 w-5 text-[#FF331F]" /> Share to Instagram</DialogTitle>
          <DialogDescription className="text-zinc-500 text-xs uppercase tracking-widest">
            Download the card · copy the caption · post it on @flow_state_method
          </DialogDescription>
        </DialogHeader>

        <Tabs value={format} onValueChange={setFormat} className="mt-2">
          <TabsList className="bg-[#050505] border border-[#27272A] rounded-sm">
            <TabsTrigger value="feed" className="data-[state=active]:bg-[#FF331F] data-[state=active]:text-white uppercase tracking-widest text-xs" data-testid="format-feed-tab">Feed (1:1)</TabsTrigger>
            <TabsTrigger value="story" className="data-[state=active]:bg-[#FF331F] data-[state=active]:text-white uppercase tracking-widest text-xs" data-testid="format-story-tab">Story (9:16)</TabsTrigger>
          </TabsList>

          <TabsContent value="feed" className="mt-4">
            <div className="relative bg-[#050505] border border-[#27272A] mx-auto" style={{ maxWidth: 420 }}>
              {generating && (
                <div className="absolute inset-0 flex items-center justify-center bg-black/60 z-10">
                  <Loader2 className="h-6 w-6 animate-spin text-[#FF331F]" />
                </div>
              )}
              <canvas ref={feedCanvasRef} className="w-full h-auto block" data-testid="feed-canvas" />
            </div>
          </TabsContent>
          <TabsContent value="story" className="mt-4">
            <div className="relative bg-[#050505] border border-[#27272A] mx-auto" style={{ maxWidth: 280 }}>
              {generating && (
                <div className="absolute inset-0 flex items-center justify-center bg-black/60 z-10">
                  <Loader2 className="h-6 w-6 animate-spin text-[#FF331F]" />
                </div>
              )}
              <canvas ref={storyCanvasRef} className="w-full h-auto block" data-testid="story-canvas" />
            </div>
          </TabsContent>
        </Tabs>

        <div className="mt-4 space-y-2">
          <div className="text-xs uppercase tracking-widest text-zinc-500 font-bold">Caption</div>
          <Textarea value={caption} onChange={(e) => setCaption(e.target.value)} rows={8} className="bg-[#050505] border-[#27272A] rounded-sm font-mono text-xs" data-testid="instagram-caption" />
        </div>

        <div className="flex flex-wrap items-center justify-end gap-2 pt-4 border-t border-[#27272A] mt-4">
          <Button type="button" variant="outline" onClick={copyCaption} className="border-[#27272A] bg-[#050505] text-white hover:bg-[#121212] rounded-sm font-bold uppercase tracking-widest" data-testid="copy-caption-btn">
            <Copy className="h-4 w-4 mr-2" /> Copy caption
          </Button>
          <Button type="button" onClick={download} disabled={generating} className="bg-white text-[#050505] hover:bg-zinc-200 rounded-sm font-bold uppercase tracking-widest" data-testid="download-image-btn">
            <Download className="h-4 w-4 mr-2" /> Download {format === "feed" ? "feed image" : "story image"}
          </Button>
          <a
            href="https://www.instagram.com/flow_state_method/"
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-2 bg-[#FF331F] hover:bg-[#FF5242] text-white font-bold uppercase tracking-widest text-sm px-4 py-2 rounded-sm"
            data-testid="open-instagram-btn"
          >
            <Instagram className="h-4 w-4" /> Open Instagram <ExternalLink className="h-3 w-3" />
          </a>
        </div>
      </DialogContent>
    </Dialog>
  );
}
